import React, { useMemo } from 'react'
import s from './styles/StepPreview.module.scss'
import { ReactComponent as MessageIcon } from 'assets/icons/message.svg'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'

const formatFullDateTime = dt => {
	if (!dt) return 'Not set'
	if (typeof dt === 'string') {
		const d = new Date(dt)
		if (Number.isNaN(d.getTime())) return 'Not set'
		return d.toLocaleString(undefined, {
			year: 'numeric',
			month: 'numeric',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}
	return dt.toLocaleString(undefined, {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

const StepPreview = ({
	preview,
	activityTitle,
	activityText,
	students = [],
	listLabel,
	timelineData = {},
	customMessage = '',
	onCustomMessageChange,
	onOpenTab = () => {},
	isEditMode,
}) => {
	const opensText = useMemo(
		() => formatFullDateTime(timelineData?.openDt),
		[timelineData?.openDt],
	)

	const closesText = useMemo(
		() => formatFullDateTime(timelineData?.closeDt),
		[timelineData?.closeDt],
	)

	const assignedCount = students?.length || 0

	return (
		<div className={s.root}>
			<header className={s.header}>
				<h2 className={s.header__title}>Review &amp; Send Assignment</h2>
				<p className={s.header__subtitle}>
					Review everything and add an optional message for students
				</p>
			</header>

			<section className={s.card}>
				<div className={s.card__titleRow}>
					<span className={s.card__icon}>
						<MessageIcon />
					</span>
					<span className={s.card__title}>Custom Message (Optional)</span>
				</div>

				<textarea
					className={s.card__textarea}
					value={customMessage}
					onChange={e => onCustomMessageChange?.(e.target.value)}
					placeholder={
						"Add a personal message to send along with this assignment...\n\nThis message will appear in each student's chat when they receive the assignment."
					}
				/>

				<p className={s.card__info}>
					Assignment will be sent without a custom message if this field is
					empty.
				</p>
			</section>

			<section className={s.card}>
				<div className={s.summary}>
					<div className={s.summary__title}>
						📋 {activityTitle || 'Form Completion'}
					</div>
					<p className={s.summary__info}>
						Assigned to{' '}
						<strong>
							{assignedCount} student{assignedCount === 1 ? '' : 's'}
						</strong>{' '}
						({listLabel || 'All Students'})
					</p>

					<div className={s.summary__date}>
						<div className={s.date}>
							<span className={s.date__icon}>
								<CalendarIcon />
							</span>
							<span className={s.date__text}>Opens</span>
							<span className={s.date__value}>{opensText}</span>
						</div>
						<div className={s.date}>
							<span className={s.date__icon}>
								<CalendarIcon />
							</span>
							<span className={s.date__text}>Closes</span>
							<span className={s.date__value}>{closesText}</span>
						</div>
					</div>
				</div>
			</section>

			<section className={s.info}>
				<div className={s.info__header}>
					<span className={s.info__title}>Description</span>
					<button type="button" className={s.edit} onClick={() => onOpenTab(3)}>
						Edit
					</button>
				</div>
				<div className={s.info__body}>
					<p className={s.info__text}>
						{activityText || 'No description provided.'}
					</p>
				</div>
			</section>

			<section className={s.info}>
				<div className={s.info__header}>
					<span className={s.info__title}>Students ({assignedCount})</span>
					<button
						type="button"
						className={`${s.edit} ${isEditMode ? s.disable : ''}`}
						onClick={() => onOpenTab(0)}
					>
						Edit
					</button>
				</div>
				<div className={s.info__body}>
					<div className={s.info__cards}>
						{students.map(st => (
							<div key={st.id || st.name} className={s.student}>
								<div className={s.student__icon}>
									<InitialAvatar
										name={st.name || 'Student'}
										size={32}
										avatarKey={st.avatarKey}
										avatarSource={st.avatarSource}
										photoUrl={st.photoUrl}
									/>
								</div>
								<div className={s.student__meta}>
									<div className={s.student__name}>{st.name || 'Student'}</div>
									<div className={s.student__info}>
										{st.grade ? `Grade ${st.grade}` : null}
										{st.section ? ` • Section ${st.section}` : null}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</div>
	)
}

export default StepPreview
